import { FC } from "react";
import { Modal } from "@app/shared/Modals";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { projectNoun } from "@app/network-portfolio-management/utils/projectTypeUtils";

interface ArchiveProjectsModalProps {
  open: boolean;
  /** Number of projects to be archived (drives the body copy and the confirm flow). */
  count: number;
  /** Disables the Archive button while a dispatch is in flight. */
  isArchiving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation modal for the bulk Archive action. Archiving is reversible — an authorized user can restore an
 * archived project from the Archived view — but it immediately pulls projects out of active views and blocks
 * running or publishing, so the modal still calls out the impact (info alert) and styles the confirm button
 * as a primary-danger (red) action. Cancel dismisses with the selection left intact.
 */
export const ArchiveProjectsModal: FC<ArchiveProjectsModalProps> = ({
  open,
  count,
  isArchiving,
  onCancel,
  onConfirm,
}) => {
  const body = (
    <div className="network-portfolio-management-archive-modal" data-testid="npm-archive-modal">
      <p className="network-portfolio-management-archive-modal__summary">
        You are about to archive <strong>{count} {projectNoun(count)}</strong>. Archived projects are removed from active
        views and cannot be run or published.
      </p>
      <QESAlert type="info" persist={true}>
        Archived projects can be restored later from the Archived view by an authorized user.
      </QESAlert>
    </div>
  );

  return (
    <Modal
      isVisible={open}
      closeModal={onCancel}
      hideBodyScroll
      content={{
        body,
        cancelButtonText: "Cancel",
        confirmButtonAction: onConfirm,
        confirmButtonDisable: isArchiving,
        confirmButtonText: "Archive",
        confirmButtonType: "primary-danger",
        title: "Archive Projects",
      }}
    />
  );
};
